import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Youtube, Play, Edit, Save, Trash2, PlusCircle, AlertTriangle, HelpCircle, Volume2 } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { showSuccess, showError } from "@/utils/toast";
import { useAuth } from "@/context/AuthContext";

interface YoutubeLink {
  id: string;
  url: string;
  userId?: number;
  channelName?: string;
  channelIcon?: string;
  hasReported?: boolean;
}

interface GameYoutubeBoxProps {
  videos: { channelName: string, hasReported: boolean, channelIcon: string, title: string; userId: number, url: string; id?: string }[];
  id: number;
}

const GameYoutubeBox: React.FC<GameYoutubeBoxProps> = ({ videos, id }) => {
  const MAX_LENGTH = 30;
  const { isTranslator, isAdmin, currentUser, isLoggedIn } = useAuth();
  const [openAction, setOpenAction] = useState<{ id: string, type: 'edit' | 'report' } | null>(null);
  const token = document.querySelector('meta[name="csrf-token"]').content;
  const [links, setLinks] = useState<YoutubeLink[]>(videos.map((v, i) => ({
    id: v.id || `yt-${i}`,
    url: v.url,
    userId: v.userId,
    channelName: v.channelName,
    channelIcon: v.channelIcon,
    hasReported: v.hasReported
  })));

  const [newLink, setNewLink] = useState("");
  const [editingLinkUrl, setEditingLinkUrl] = useState("");

  const audioRef = useRef<HTMLAudioElement | null>(new Audio('/2.mp3')); // Initialize once
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) { // Check if audio is not null
      const handleAudioEnd = () => setIsPlayingAudio(false);
      const handleAudioError = () => {
        setIsPlayingAudio(false);
        showError("Hiba történt a hangfájl lejátszása során.");
      };

      audio.addEventListener('ended', handleAudioEnd);
      audio.addEventListener('error', handleAudioError);
      audio.loop = false; // Ensure it does not loop automatically

      return () => {
        audio.pause();
        audio.currentTime = 0;
        audio.removeEventListener('ended', handleAudioEnd);
        audio.removeEventListener('error', handleAudioError);
      };
    }
  }, []); // Empty dependency array ensures this only runs on mount and unmount.

  function shortenText(text: string | undefined | null, maxLength: number) {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength - 1) + "…";
  }

  const handlePlayAudio = () => {
    const audio = audioRef.current;
    if (!audio) return; // Should not happen if initialized with new Audio()

    if (isPlayingAudio) {
      audio.pause();
      audio.currentTime = 0;
      setIsPlayingAudio(false);
    } else {
      audio.play().catch(error => {
        console.warn("Audio play was prevented.", error);
        showError("A hang lejátszása nem indítható el automatikusan.");
      });
      setIsPlayingAudio(true);
    }
  };

  // ÚJ LINK FELVÉTELE
  const handleAddLink = async () => {
    if (!newLink.trim()) {
      showError("A link mező nem lehet üres!");
      return;
    }

    // Megengedőbb validáció, ami a legtöbb YouTube linket elfogadja
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
    if (!youtubeRegex.test(newLink)) {
      showError("Érvénytelen YouTube link. A linknek a youtube.com vagy youtu.be domainre kell mutatnia.");
      return;
    }

    try {
      const res = await fetch(`/api/youtube_links`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token
        },
        body: JSON.stringify({
          youtube_link: { url: newLink.trim(), game_id: id }   // 👉 FONTOS: küldd a game_id-t!
        })
      });

      if (!res.ok) {
        const errData = await res.json();
        showError(errData.errors?.join(", ") || "Hiba történt a link hozzáadásakor.");
        return;
      }

      const createdLink = await res.json();

      // 👉 itt már a backend által visszaküldött adatokból építjük a state-et
      setLinks([...links, {
        id: createdLink.id,                 // Rails-nek laposan kell visszaadnia az id-t
        url: createdLink.url,
        channelName: createdLink.channelName || "Ismeretlen csatorna",
        channelIcon: createdLink.channelIcon || undefined,
        hasReported: createdLink.hasReported,
      }]);

      setNewLink('');
      showSuccess("YouTube link sikeresen hozzáadva!");
    } catch (err) {
      showError("Hálózati hiba: Nem sikerült elküldeni a linket.");
    }
  };

  // LINK SZERKESZTÉS MENTÉSE
  const handleSaveEdit = async () => {
    if (!editingLinkUrl.trim()) {
      showError("A link mező nem lehet üres!");
      return;
    }
    if (!openAction?.id) return;

    try {
      const res = await fetch(`/api/youtube_links/${openAction.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          'X-CSRF-Token': token
        },
        body: JSON.stringify({
          youtube_link: { url: editingLinkUrl.trim() }
        })
      });

      if (!res.ok) {
        const errData = await res.json();
        showError(errData.errors?.join(", ") || "Hiba történt a mentésnél.");
        return;
      }

      const updatedLink = await res.json();
      setLinks(links.map(link =>
        link.id === openAction.id ? { ...link, url: updatedLink.url } : link
      ));
      setOpenAction(null);
      showSuccess("Link sikeresen frissítve!");
    } catch (err) {
      showError("Hálózati hiba: Nem sikerült menteni.");
    }
  };

  // LINK TÖRLÉSE
  const handleDeleteLink = async (id: string) => {
    try {
      const res = await fetch(`/api/youtube_links/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          'X-CSRF-Token': token
        }
      });

      if (!res.ok) {
        const errData = await res.json();
        showError(errData.errors?.join(", ") || "Hiba történt a törlésnél.");
        return;
      }

      setLinks(links.filter(link => link.id !== id));
      showSuccess("Link sikeresen törölve!");
    } catch (err) {
      showError("Hálózati hiba: Nem sikerült törölni.");
    }
  };

  const handleEditClick = (link: YoutubeLink) => {
    if (openAction?.id === link.id && openAction?.type === 'edit') {
      setOpenAction(null);
    } else {
      setOpenAction({ id: link.id, type: 'edit' });
      setEditingLinkUrl(link.url);
    }
  };

  const handleReportClick = (link: YoutubeLink) => {
    if (openAction?.id === link.id && openAction?.type === 'report') {
      setOpenAction(null);
    } else {
      setOpenAction({ id: link.id, type: 'report' });
    }
  }

  const handleConfirmReport = async (link: YoutubeLink) => {
    if (!currentUser) {
      showError("Be kell jelentkezned a jelentéshez!");
      return;
    }

    try {
      const res = await fetch('/api/youtube_link_reports', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify({
          youtube_link_report: {
            game_id: id,
            youtube_link_id: link.id
          }
        })
      });

      if (!res.ok) {
        const data = await res.json();
        showError(data.errors?.join(', ') || 'Hiba történt a jelentés során.');
        return;
      }

      showSuccess('A jelentés sikeresen elküldve. Köszönjük a visszajelzést!');
      setOpenAction(null);
    } catch {
      showError('Hálózati hiba történt a jelentés küldésekor.');
    }
  };

  return (
    <Card className="bg-secondary text-secondary-foreground border-border shadow-md">
      <CardHeader>
        <CardTitle className="text-xl font-bold text-primary flex items-center gap-2">
          <Youtube size={24} className="text-red-500" /> YouTube videók
          <Button
            onClick={handlePlayAudio}
            variant="secondary"
            size="icon"
            className="ml-2 h-16 w-16 animate-pulse"
            title="Tudni valók (hang)"
          >
            {isPlayingAudio ? <Volume2 size={32} /> : <HelpCircle size={32} />}
          </Button>
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col gap-3">
        {/* LINK LISTA */}
        <div className="space-y-2">
          {links.map((link) => (
            <Collapsible
              key={link.id}
              open={openAction?.id === link.id}
              onOpenChange={(isOpen) => !isOpen && setOpenAction(null)}
            >
              <div className="flex items-center justify-between p-2 rounded-md bg-card">

                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary truncate"
                >
                  {link.channelIcon ? (
                    <img
                      src={link.channelIcon}
                      alt={`${link.channelName} ikon`}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <Play size={16} className="text-red-500" />
                  )}
                  <span className="truncate">
                    {shortenText(link.channelName, MAX_LENGTH)}
                  </span>
                </a>

                <div className="flex gap-1">
                  {/* Csak a fordítók szerkeszthetnek */}
                  {isLoggedIn && (
                    !link.hasReported ? (
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-yellow-500 hover:text-yellow-600"
                          onClick={() => handleReportClick(link)}
                          title="Videó jelentése"
                        >
                          <AlertTriangle size={14} />
                        </Button>
                      </CollapsibleTrigger>
                    ) : (
                      <div className="flex items-center text-red-600 font-semibold" title="Jelentve">
                        <AlertTriangle size={14} />
                        <span className="ml-1">jelentve</span>
                      </div>
                    )
                  )}
                  {(isAdmin || (isTranslator && link.userId === currentUser?.id)) && (
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEditClick(link)}
                      >
                        <Edit size={14} />
                      </Button>
                    </CollapsibleTrigger>
                  )}

                  {/* Csak az adminok törölhetnek */}
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleDeleteLink(link.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </div>

              <CollapsibleContent className="p-3 mt-1 border border-border rounded-md bg-secondary">

                {openAction?.type === 'edit' && (
                  <div className="space-y-2">
                    <Input
                      type="text"
                      value={editingLinkUrl}
                      onChange={(e) => setEditingLinkUrl(e.target.value)}
                      className="bg-card text-card-foreground"
                      placeholder="YouTube link"
                    />
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => setOpenAction(null)}>Mégsem</Button>
                      <Button size="sm" onClick={handleSaveEdit} className="bg-primary text-primary-foreground">
                        <Save size={14} className="mr-1" /> Mentés
                      </Button>
                    </div>
                  </div>
                )}
                {openAction?.type === 'report' && (
                  <div className="space-y-2">
                    <p className="text-sm text-yellow-300">Biztosan jelenteni szeretnéd ezt a videót, mert nem megfelelő?</p>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => setOpenAction(null)}>Mégsem</Button>
                      <Button size="sm" onClick={() => handleConfirmReport(link)} className="bg-destructive text-destructive-foreground">
                        <AlertTriangle size={14} className="mr-1" /> Bejelentem
                      </Button>
                    </div>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>

        {/* ÚJ LINK HOZZÁADÁSA */}
        {isTranslator && (
          <div className="border-t border-border pt-3 mt-2">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Új YouTube link..."
                value={newLink}
                onChange={(e) => setNewLink(e.target.value)}
                className="bg-card text-card-foreground"
              />
              <Button
                onClick={handleAddLink}
                className="bg-primary text-primary-foreground"
              >
                <PlusCircle size={16} />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GameYoutubeBox;
